import React, { useState } from 'react';
import {
    ChevronDown,
    ChevronUp,
    CheckCircle,
    AlertTriangle,
    Search,
    ThumbsUp,
    RotateCcw,
    ShieldAlert,
    X
} from 'lucide-react';
import TranslationTooltip from './TranslationTooltip';

interface TestCaseCardProps {
    caso: any;
    isExpanded: boolean;
    onToggle: () => void;
    onShowXray: (evidence: any) => void;
    onStatusChange?: (id: string, status: 'approved' | 'blocked' | 'draft', reason?: string) => void;
    onStepStatusChange?: (stepId: number | string, status: string, explanation?: string) => void;
}

const BLOCK_QUICK_REASONS = [
    { label: 'Falta evidencia', icon: '📄' },
    { label: 'Ambiguo / contradictorio', icon: '⚠️' },
    { label: 'Requiere definición de negocio', icon: '🏢' },
];

const TestCaseCard: React.FC<TestCaseCardProps> = ({ caso, isExpanded, onToggle, onShowXray, onStatusChange, onStepStatusChange }) => {
    const [isUpdating, setIsUpdating] = useState(false);
    const [showBlockModal, setShowBlockModal] = useState(false);
    const [blockReason, setBlockReason] = useState('');
    const [blockDetail, setBlockDetail] = useState('');
    const [toast, setToast] = useState<string | null>(null);
    const normalizedStatus = (caso.review_status || caso.status || 'draft').toLowerCase();

    // Check if all steps are approved
    const allStepsApproved = Array.isArray(caso.pasos) && caso.pasos.every((p: any) => (p.review_status || 'DRAFT').toUpperCase() === 'APPROVED');

    const handleStatusClick = async (e: React.MouseEvent, status: 'approved' | 'blocked' | 'draft') => {
        e.stopPropagation();
        if (onStatusChange) {
            if (status === 'blocked') {
                setBlockReason('');
                setBlockDetail('');
                setShowBlockModal(true);
                return;
            } else if (status === 'approved' && !allStepsApproved) {
                const confirm = window.confirm("⚠️ No todos los pasos están aprobados.\n\n¿Deseas aprobar el caso de todas formas?\n(Lo ideal es aprobar cada paso individualmente primero)");
                if (!confirm) return;
            }

            setIsUpdating(true);
            try {
                await onStatusChange(caso.id_caso, status);
            } finally {
                setIsUpdating(false);
            }
        }
    };

    const handleBlockConfirm = async () => {
        if (!onStatusChange) return;
        const fullReason = [blockReason, blockDetail].filter(Boolean).join(' — ') || 'Bloqueado manualmente';
        setIsUpdating(true);
        setShowBlockModal(false);
        try {
            await onStatusChange(caso.id_caso, 'blocked', fullReason);
            setToast(`Caso bloqueado: ${blockReason || blockDetail}`);
            setTimeout(() => setToast(null), 3500);
        } finally {
            setIsUpdating(false);
        }
    };

    const handleStepAction = async (e: React.MouseEvent, stepId: number | string, status: string) => {
        e.stopPropagation();
        if (!onStepStatusChange) return;

        let explanation = undefined;
        if (status === 'BLOCKED' || status === 'NEEDS_CHANGE') {
            const input = window.prompt("Razón del cambio (Opcional):");
            if (input === null) return;
            explanation = input;
        }
        await onStepStatusChange(stepId, status, explanation);
    };

    const getPriorityColor = (priority: string) => {
        switch (priority?.toLowerCase()) {
            case 'alta': return 'bg-red-500/10 text-red-400 border-red-500/20';
            case 'media': return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20';
            case 'baja': return 'bg-green-500/10 text-green-400 border-green-500/20';
            default: return 'bg-slate-500/10 text-slate-400 border-slate-500/20';
        }
    };

    const getTypeColor = (tipo: string) => {
        const t = (tipo || '').toLowerCase();
        if (t.includes('positivo')) return 'bg-green-500/10 text-green-400';
        if (t.includes('negativo')) return 'bg-red-500/10 text-red-400';
        if (t.includes('borde')) return 'bg-purple-500/10 text-purple-400';
        return 'bg-blue-500/10 text-blue-400';
    };

    const getModeColor = (mode?: string) => {
        if (!mode) return 'bg-slate-500/10 text-slate-400 border-slate-500/20';
        switch (mode.toLowerCase()) {
            case 'strict': return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
            case 'reco': return 'bg-purple-500/10 text-purple-400 border-purple-500/20';
            default: return 'bg-slate-500/10 text-slate-400 border-slate-500/20';
        }
    };

    const getStatusColor = (status?: string) => {
        switch (status?.toLowerCase()) {
            case 'approved': return 'bg-green-500/20 text-green-400 border-green-500/30';
            case 'blocked': return 'bg-red-500/20 text-red-400 border-red-500/30';
            case 'rework': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
            case 'in_review': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
            default: return 'bg-slate-700 text-slate-400 border-slate-600'; // Draft
        }
    };

    const getStepStatusColor = (status?: string) => {
        switch (status?.toUpperCase()) {
            case 'APPROVED': return 'bg-green-500/20 text-green-300 border-green-500/30';
            case 'BLOCKED': return 'bg-red-500/20 text-red-300 border-red-500/30';
            case 'NEEDS_CHANGE': return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
            default: return 'bg-slate-700 text-slate-400 border-slate-600';
        }
    };

    const getStepStatusIcon = (status?: string) => {
        switch (status?.toUpperCase()) {
            case 'APPROVED': return <CheckCircle className="w-3 h-3" />;
            case 'BLOCKED': return <AlertTriangle className="w-3 h-3" />;
            case 'NEEDS_CHANGE': return <RotateCcw className="w-3 h-3" />;
            default: return <span className="text-[10px] font-bold">DRAFT</span>;
        }
    };

    const getStatusIcon = (status?: string) => {
        switch (status?.toLowerCase()) {
            case 'approved': return <ThumbsUp className="w-3 h-3" />;
            case 'blocked': return <AlertTriangle className="w-3 h-3" />;
            case 'in_review': return <Search className="w-3 h-3" />;
            default: return null;
        }
    };

    const getModeLabel = (mode?: string) => {
        if (!mode) return null;
        return mode.toLowerCase() === 'strict' ? 'STRICT' : 'RECO';
    };

    return (
        <div className={`bg-surface-dark border rounded-xl overflow-hidden transition-all ${normalizedStatus === 'approved' ? 'border-green-500/30' :
            normalizedStatus === 'blocked' ? 'border-red-500/30' : 'border-slate-800'
            }`}>
            {/* Header */}
            <button
                onClick={onToggle}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-800/50 transition-colors"
            >
                <div className="flex items-center gap-4 flex-1 min-w-0">
                    {/* Status Indicator (Left) */}
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${normalizedStatus === 'approved' ? 'bg-green-500' :
                        normalizedStatus === 'blocked' ? 'bg-red-500' : 'bg-slate-600'
                        }`} />

                    <span className="text-sm font-mono text-primary flex-shrink-0">{caso.id_caso}</span>

                    {/* Title with Translation Tooltip */}
                    <TranslationTooltip
                        title={caso.titulo || caso.title || ''}
                        objective={caso.objetivo || caso.objective || ''}
                    >
                        <h4 className="text-white font-medium text-left truncate max-w-[400px] cursor-help" title={caso.titulo}>
                            {caso.titulo}
                            <span className="ml-1.5 text-slate-600 text-xs" title="Hover para ver traducción al español">🌐</span>
                        </h4>
                    </TranslationTooltip>

                    <div className="flex items-center gap-2 flex-shrink-0">
                        {normalizedStatus !== 'draft' && (
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold border flex items-center gap-1 uppercase ${getStatusColor(normalizedStatus)}`}>
                                {getStatusIcon(normalizedStatus)} {normalizedStatus.replace('_', ' ')}
                            </span>
                        )}
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getTypeColor(caso.tipo)}`}>
                            {caso.tipo}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(caso.prioridad)}`}>
                            {caso.prioridad}
                        </span>
                        {getModeLabel(caso.mode) && (
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${getModeColor(caso.mode)}`}>
                                {getModeLabel(caso.mode)}
                            </span>
                        )}
                    </div>
                </div>
                {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                )}
            </button>

            {/* Content */}
            {isExpanded && (
                <div className="px-6 pb-6 space-y-4 border-t border-slate-800 pt-4">

                    {/* Workflow Actions */}
                    {onStatusChange && (
                        <div className="flex justify-end gap-2 mb-4 pb-4 border-b border-slate-800/50">
                            <button
                                onClick={(e) => handleStatusClick(e, 'approved')}
                                disabled={isUpdating || normalizedStatus === 'approved'}
                                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${normalizedStatus === 'approved'
                                    ? 'bg-green-500/20 text-green-300 cursor-default'
                                    : 'bg-slate-800 text-slate-300 hover:bg-green-500/20 hover:text-green-300'
                                    } ${!allStepsApproved && normalizedStatus !== 'approved' ? 'opacity-80 ring-1 ring-yellow-500/30' : ''}`}
                                title={!allStepsApproved ? "Warning: Not all steps are approved" : "Approve Case"}
                            >
                                <ThumbsUp className="w-4 h-4" /> {allStepsApproved ? "Approve Case" : "Approve (Warning)"}
                            </button>
                            <button
                                onClick={(e) => handleStatusClick(e, 'blocked')}
                                disabled={isUpdating || normalizedStatus === 'blocked'}
                                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${normalizedStatus === 'blocked'
                                    ? 'bg-red-500/20 text-red-300 cursor-default'
                                    : 'bg-slate-800 text-slate-300 hover:bg-red-500/20 hover:text-red-300'
                                    }`}
                            >
                                <AlertTriangle className="w-4 h-4" /> Block
                            </button>
                            {normalizedStatus !== 'draft' && (
                                <button
                                    onClick={(e) => handleStatusClick(e, 'draft')}
                                    disabled={isUpdating}
                                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white transition-colors"
                                    title="Reset to Draft"
                                >
                                    <RotateCcw className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    )}

                    {/* Objetivo */}
                    <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Objetivo</p>
                        <p className="text-slate-300">{caso.objetivo}</p>
                    </div>

                    {/* Precondiciones */}
                    {caso.precondiciones && (
                        <div>
                            <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Precondiciones</p>
                            <p className="text-slate-300">{caso.precondiciones}</p>
                        </div>
                    )}

                    {/* Datos de Prueba */}
                    {caso.datos_prueba && (
                        <div>
                            <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Datos de Prueba</p>
                            <p className="text-slate-300">{caso.datos_prueba}</p>
                        </div>
                    )}

                    {/* Pasos */}
                    <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Pasos</p>
                        <div className="space-y-2">
                            {Array.isArray(caso.pasos) ? caso.pasos.map((paso: any, idx: number) => {
                                const stepStatus = (paso.review_status || 'DRAFT').toUpperCase();
                                const stepId = paso.id || idx;
                                const apiStepId = paso.db_id || paso.id || idx; // Use DB UUID for API calls
                                const hasEvidence = paso.evidence_refs && paso.evidence_refs.length > 0;
                                const evidenceCount = paso.evidence_refs ? paso.evidence_refs.length : 0; // Use count

                                return (
                                    <div
                                        key={stepId}
                                        className={`flex gap-3 bg-slate-800/50 rounded-lg p-3 group relative hover:bg-slate-800 transition-colors border cursor-pointer ${stepStatus === 'APPROVED' ? 'border-green-500/20' : stepStatus === 'BLOCKED' ? 'border-red-500/20' : 'border-transparent hover:border-slate-700'}`}
                                        onClick={() => {
                                            // Make step clickable to show evidence if available, or just details
                                            if (hasEvidence) {
                                                onShowXray(paso.evidence_refs[0]); // Pass object
                                            }
                                        }}
                                    >
                                        {/* Step ID / Status Indicator */}
                                        <div className="flex flex-col items-center gap-2 pt-1">
                                            <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold bg-slate-700 text-slate-400`} title={`Paso ${stepId}`}>
                                                {stepId}
                                            </span>
                                            {/* Status Chip */}
                                            <span className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase font-bold border ${getStepStatusColor(stepStatus)}`}>
                                                {getStepStatusIcon(stepStatus)}
                                            </span>
                                        </div>

                                        <div className="flex-1">
                                            <div className="flex justify-between items-start">
                                                <p className="text-slate-300 font-medium">
                                                    {typeof paso.accion === 'object' ? JSON.stringify(paso.accion) : paso.accion}
                                                </p>

                                                {/* Actions Overlay/Sidebar */}
                                                <div className="flex items-center gap-2">
                                                    {/* Evidence CTA */}
                                                    {hasEvidence ? (
                                                        <span className="px-2 py-1 bg-blue-500/10 text-blue-400 rounded text-xs flex items-center gap-1 border border-blue-500/20">
                                                            <Search className="w-3 h-3" /> View Evidence ({evidenceCount})
                                                        </span>
                                                    ) : (
                                                        <span className="text-slate-600 text-xs flex items-center gap-1" title="Evidence is missing">
                                                            <Search className="w-3 h-3" /> Find Evidence
                                                        </span>
                                                    )}

                                                    {onStepStatusChange && (
                                                        <div className="flex items-center gap-1 ml-2 border-l border-slate-700 pl-2" onClick={(e) => e.stopPropagation()}>
                                                            <button
                                                                onClick={(e) => handleStepAction(e, apiStepId, 'APPROVED')}
                                                                className={`p-1.5 rounded hover:bg-green-500/20 hover:text-green-400 transition-colors ${stepStatus === 'APPROVED' ? 'text-green-400 bg-green-500/10' : 'text-slate-500'}`}
                                                                title="Mark as Verified"
                                                            >
                                                                <ThumbsUp className="w-3.5 h-3.5" />
                                                            </button>
                                                            <button
                                                                onClick={(e) => handleStepAction(e, apiStepId, 'BLOCKED')}
                                                                className={`p-1.5 rounded hover:bg-red-500/20 hover:text-red-400 transition-colors ${stepStatus === 'BLOCKED' ? 'text-red-400 bg-red-500/10' : 'text-slate-500'}`}
                                                                title="Block Step"
                                                            >
                                                                <AlertTriangle className="w-3.5 h-3.5" />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <p className="text-sm text-slate-400 mt-2 flex items-start gap-2">
                                                <CheckCircle className="w-3.5 h-3.5 text-green-500/50 mt-0.5 flex-shrink-0" />
                                                {typeof paso.resultado_esperado === 'object' ? JSON.stringify(paso.resultado_esperado) : paso.resultado_esperado}
                                            </p>

                                            {paso.explanation && (
                                                <p className="text-xs text-orange-400 mt-2 italic border-l-2 border-orange-500/30 pl-2">
                                                    Note: {typeof paso.explanation === 'object' ? JSON.stringify(paso.explanation) : paso.explanation}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )
                            }) : (
                                <p className="text-slate-500 text-sm italic">No steps available or invalid format.</p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Block Reason Modal */}
            {showBlockModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowBlockModal(false)}>
                    <div className="bg-slate-900 border border-red-500/30 rounded-xl p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2 text-red-400">
                                <ShieldAlert className="w-5 h-5" />
                                <h3 className="font-semibold text-lg">Bloquear Caso</h3>
                            </div>
                            <button onClick={() => setShowBlockModal(false)} className="p-1 rounded hover:bg-slate-800 text-slate-500 hover:text-white transition-colors">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <p className="text-slate-400 text-sm mb-4">Selecciona una razón o escribe una personalizada:</p>

                        {/* Quick Reasons */}
                        <div className="flex flex-wrap gap-2 mb-4">
                            {BLOCK_QUICK_REASONS.map(r => (
                                <button
                                    key={r.label}
                                    onClick={() => setBlockReason(blockReason === r.label ? '' : r.label)}
                                    className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all ${blockReason === r.label
                                        ? 'bg-red-500/20 border-red-500/50 text-red-300 shadow-lg shadow-red-500/10'
                                        : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600 hover:text-slate-300'
                                        }`}
                                >
                                    <span className="mr-1.5">{r.icon}</span>{r.label}
                                </button>
                            ))}
                        </div>

                        {/* Detail Field */}
                        <textarea
                            value={blockDetail}
                            onChange={e => setBlockDetail(e.target.value)}
                            placeholder="Ej: Falta definir SLA de timeout (30s) y canal de alerta."
                            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-slate-300 placeholder:text-slate-600 focus:outline-none focus:border-red-500/50 focus:ring-1 focus:ring-red-500/20 resize-none transition-colors"
                            rows={3}
                        />

                        {/* Impact Notice */}
                        <div className="mt-4 px-3 py-2.5 bg-amber-500/5 border border-amber-500/15 rounded-lg">
                            <p className="text-xs text-slate-400 leading-relaxed">
                                ⚠️ Este caso quedará en estado <span className="text-red-400 font-bold">BLOQUEADO</span> y no podrá aprobarse hasta resolver la causa.
                            </p>
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end gap-2 mt-4">
                            <button
                                onClick={() => setShowBlockModal(false)}
                                className="px-4 py-2 rounded-lg text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleBlockConfirm}
                                disabled={!blockReason && !blockDetail}
                                className="px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                <AlertTriangle className="w-4 h-4" />
                                Confirmar Bloqueo
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast Notification */}
            {toast && (
                <div className="fixed bottom-6 right-6 z-50 animate-[fadeIn_0.3s_ease-out] bg-slate-800 border border-red-500/30 text-slate-200 px-4 py-3 rounded-xl shadow-2xl text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                    {toast}
                </div>
            )}
        </div>
    );
};

export default TestCaseCard;
